import React, { useState } from 'react'
import { 
  XMarkIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import axiosInstance from '../../api/axiosInstance'
import toast from 'react-hot-toast'

interface OfimaProducto {
  ErrorNumber: number
  ErrorMessage: string
  IdProducto: string
  NombreProducto: string
  Lote: string
  CodigoBarras: string
  Componentes: string
  stockComponentes: string
  Saldo: number
  SumaSaldo: number
  SaldoDisponible: number
  IdUnidad: string
  PctIVA: number
  Venta_General: number | null
  Bigcenter: number | null
  Distrito: number | null
  Mercadolibre: number | null
  Falabella: number | null
  Homecenter: number | null
  Exito: number | null
  Euro: number | null
}

interface CreateMissingProductsModalProps {
  isOpen: boolean
  onClose: () => void
  errorSkus: string[]
  ofimaProducts: OfimaProducto[]
}

const CreateMissingProductsModal: React.FC<CreateMissingProductsModalProps> = ({
  isOpen,
  onClose,
  errorSkus,
  ofimaProducts
}) => {
  const [creatingProducts, setCreatingProducts] = useState<Set<string>>(new Set())
  const [createdProducts, setCreatedProducts] = useState<Set<string>>(new Set())
  const [creationErrors, setCreationErrors] = useState<{ sku: string; error: string }[]>([])
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [selectedProductJson, setSelectedProductJson] = useState<any>(null)

  // Filtrar productos de Ofima que están en la lista de errores
  const missingProducts = ofimaProducts.filter(product => 
    errorSkus.includes(product.IdProducto)
  )

  // Función para obtener el precio según la jerarquía de prioridad
  const getPriceByPriority = (product: OfimaProducto): number | null => {
    const priceSources = [
      { name: 'Bigcenter', price: product.Bigcenter },
      { name: 'Distrito', price: product.Distrito },
      { name: 'Venta_General', price: product.Venta_General },
      { name: 'Falabella', price: product.Falabella },
      { name: 'Mercadolibre', price: product.Mercadolibre },
      { name: 'Homecenter', price: product.Homecenter }
    ]
    
    for (const source of priceSources) {
      if (source.price !== null && source.price !== undefined && source.price > 0) {
        return source.price
      }
    }
    
    return null
  }

  // Función para procesar componentes de un kit
  const processKitComponents = (product: OfimaProducto) => {
    if (!product.Componentes || !product.stockComponentes) {
      return []
    }

    const componentSkus = product.Componentes.split(',').map(sku => sku.trim())
    const componentStocks = product.stockComponentes.split(',').map(stock => {
      // Convertir "1.0000000" a 1, "2.0000000" a 2, etc.
      const numericStock = parseFloat(stock.trim())
      return Math.round(numericStock) // Redondear para eliminar decimales innecesarios
    })

    // Crear array de componentes con SKU y cantidad
    const components = componentSkus.map((sku, index) => ({
      sku: sku,
      quantity: componentStocks[index] || 1 // Si no hay cantidad, usar 1 por defecto
    }))

    return components
  }

  // Función para transformar producto de Ofima al formato requerido
  const transformOfimaProduct = (product: OfimaProducto) => {
    const price = getPriceByPriority(product)
    const hasComponents = product.Componentes && product.Componentes.trim() !== ''
    const components = hasComponents ? processKitComponents(product) : []
    
    return {
      sku: product.IdProducto,
      name: product.NombreProducto,
      description: product.NombreProducto, // Usar el nombre como descripción por defecto
      category_id: 1, // Categoría por defecto
      price: price || 0,
      is_combo: hasComponents && components.length >= 1, // Es combo si tiene al menos 1 componente
      tax_rate: product.PctIVA / 100, // Convertir porcentaje a decimal
      ean12: product.CodigoBarras || '',
      peso: '', // No disponible en Ofima
      longitud: '', // No disponible en Ofima
      ancho: '', // No disponible en Ofima
      alto: '', // No disponible en Ofima
      instrucciones_empaque: '', // No disponible en Ofima
      imagen: '', // No disponible en Ofima
      components: components // Componentes procesados del kit
    }
  }

  // Función para mostrar el JSON que se enviará
  const handleShowJson = (product: OfimaProducto) => {
    const transformedProduct = transformOfimaProduct(product)
    setSelectedProductJson(transformedProduct)
    setShowJsonModal(true)
  }

  // Función para crear un producto individual
  const handleCreateProduct = async (product: OfimaProducto) => {
    const sku = product.IdProducto
    
    // Verificar si ya está siendo creado o ya fue creado
    if (creatingProducts.has(sku) || createdProducts.has(sku)) {
      return
    }

    setCreatingProducts(prev => new Set(prev).add(sku))
    
    try {
      const productToCreate = transformOfimaProduct(product)
      await axiosInstance.post('/products', productToCreate)
      
      setCreatedProducts(prev => new Set(prev).add(sku))
      toast.success(`Producto ${sku} creado exitosamente`)
      
      // Remover error si existía
      setCreationErrors(prev => prev.filter(error => error.sku !== sku))
      
    } catch (error: any) {
      console.error(`Error creando producto ${sku}:`, error)
      setCreationErrors(prev => [...prev, {
        sku: sku,
        error: error.response?.data?.message || 'Error desconocido'
      }])
      toast.error(`Error creando producto ${sku}`)
    } finally {
      setCreatingProducts(prev => {
        const newSet = new Set(prev)
        newSet.delete(sku)
        return newSet
      })
    }
  }

  // Resetear estado al cerrar
  const handleClose = () => {
    setCreatingProducts(new Set())
    setCreatedProducts(new Set())
    setCreationErrors([])
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-custom-blue">
            Crear Productos Faltantes ({missingProducts.length})
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Resumen de estado */}
        {(createdProducts.size > 0 || creationErrors.length > 0) && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircleIcon className="h-4 w-4" />
                <span>Creados: {createdProducts.size}</span>
              </div>
              <div className="flex items-center gap-2 text-red-600">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>Errores: {creationErrors.length}</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabla de productos */}
        <div className="overflow-auto max-h-96 border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Precio
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IVA
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Componentes
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Componentes Procesados
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {missingProducts.map((product) => {
                const sku = product.IdProducto
                const price = getPriceByPriority(product)
                const hasComponents = product.Componentes && product.Componentes.trim() !== ''
                const processedComponents = hasComponents ? processKitComponents(product) : []
                const isCreating = creatingProducts.has(sku)
                const isCreated = createdProducts.has(sku)
                const hasError = creationErrors.some(error => error.sku === sku)
                
                return (
                  <tr key={sku} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {sku}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={product.NombreProducto}>
                      {product.NombreProducto}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {price ? `$${price.toLocaleString('es-CO')}` : 'Sin precio'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {product.PctIVA}%
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {product.Saldo}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {hasComponents ? (
                        <span className="text-green-600 font-medium">Sí ({product.Componentes.split(',').length})</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {processedComponents.length > 0 ? (
                        <div className="max-w-xs">
                          {processedComponents.map((comp, index) => (
                            <div key={index} className="text-xs">
                              <span className="font-medium">{comp.sku}</span>: {comp.quantity}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {isCreating ? (
                        <span className="flex items-center gap-1 text-blue-600">
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Creando...
                        </span>
                      ) : isCreated ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircleIcon className="h-4 w-4" />
                          Creado
                        </span>
                      ) : hasError ? (
                        <span className="flex items-center gap-1 text-red-600">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          Error
                        </span>
                      ) : (
                        <span className="text-gray-500">Pendiente</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        {isCreated ? (
                          <span className="text-green-600 font-medium">Completado</span>
                        ) : (
                          <button
                            onClick={() => handleCreateProduct(product)}
                            disabled={isCreating}
                            className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            {isCreating ? (
                              <>
                                <ArrowPathIcon className="h-3 w-3 animate-spin" />
                                Creando...
                              </>
                            ) : (
                              <>
                                <PlusIcon className="h-3 w-3" />
                                Crear
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleShowJson(product)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                          title="Ver JSON que se enviará"
                        >
                          📄 JSON
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Errores de creación */}
        {creationErrors.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-sm font-medium text-red-800 mb-2">Errores de Creación:</h3>
            <div className="max-h-32 overflow-y-auto">
              {creationErrors.map((error, index) => (
                <div key={index} className="text-xs text-red-700 mb-1">
                  <span className="font-medium">{error.sku}:</span> {error.error}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Modal para mostrar JSON */}
      {showJsonModal && selectedProductJson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-custom-blue">
                JSON que se enviará para: {selectedProductJson.sku}
              </h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="bg-gray-100 rounded-lg p-4 overflow-auto max-h-96">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(selectedProductJson, null, 2)}
              </pre>
            </div>
            
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowJsonModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreateMissingProductsModal
